import { useCallback, useEffect, useRef } from 'react';

import { ILocation } from '@rbi-ctg/frontend';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useUpdateOnAppStateChange } from 'hooks/use-update-on-app-state-change';
import { useGeolocation } from 'state/geolocation';

import {
  StoreLocatorAction,
  StoreLocatorActionType,
  UseStoreLocatorInitProps,
} from '../components/types';
import { IStore } from '../ducks/stores';

/**
 * A hook that handles the initialization of the store locator map and list view.
 * - Focuses the map on the appropriate coordinates (active store, user location, or default)
 * - Highlights the active store in the list if one exists
 * - Scrolls the list view to the active store
 * - This hook is triggered when the user navigates away from the app and then returns
 *  ie: when the user changes the permission
 *
 */
export const useStoreLocatorInit = ({
  mapRef,
  activeStore,
  nearbyStore,
  onRestaurantHighlight,
  actions,
  onSearchAllStores,
  onSearchNearbyAsync,
}: UseStoreLocatorInitProps) => {
  const mapIsReady = mapRef.current?.mapIsReady;
  const hasCalledInitialLoad = useRef(false);
  const { loadCurrentPositionAsync, isPermissionGranted, isLoadingPermission } = useGeolocation();

  const handleHighlightStore = useCallback(
    (coords?: ILocation, store?: IStore) => {
      const selectedStore = activeStore ?? nearbyStore ?? store;
      onRestaurantHighlight({ restaurant: selectedStore, coords });
    },
    [onRestaurantHighlight, activeStore, nearbyStore]
  );

  const initializeWithoutLocation = useCallback(() => {
    onSearchAllStores();
    handleHighlightStore();
  }, [onSearchAllStores, handleHighlightStore]);

  const initializeWithLocation = useCallback(async () => {
    const coordinates = await loadCurrentPositionAsync();
    let selectedStore = activeStore ?? nearbyStore;
    if (coordinates) {
      mapRef.current?.focus({
        coordinates,
        focusReason: StoreLocatorAction.isInitial,
      });

      if (!selectedStore) {
        const nearbyStores = await onSearchNearbyAsync(coordinates);
        selectedStore = nearbyStores?.[0];
      }
      onSearchAllStores(coordinates);
      handleHighlightStore(coordinates, selectedStore);
    } else {
      initializeWithoutLocation();
    }
  }, [
    activeStore,
    nearbyStore,
    loadCurrentPositionAsync,
    onSearchAllStores,
    mapRef,
    handleHighlightStore,
    onSearchNearbyAsync,
    initializeWithoutLocation,
  ]);

  const resetInitializationState = useCallback(() => {
    hasCalledInitialLoad.current = false;
    actions.current[StoreLocatorActionType.INITIAL_LOAD] = false;
  }, [actions]);

  useEffect(() => {
    if (!mapIsReady || !!hasCalledInitialLoad.current || isLoadingPermission) {
      return;
    }
    actions.current[StoreLocatorActionType.INITIAL_LOAD] = true;
    hasCalledInitialLoad.current = true;
    if (isPermissionGranted) {
      initializeWithLocation();
    } else {
      initializeWithoutLocation();
    }
  }, [
    initializeWithLocation,
    initializeWithoutLocation,
    isLoadingPermission,
    isPermissionGranted,
    mapIsReady,
    actions,
  ]);

  // Forces the initialization to reset if the app is not active
  useUpdateOnAppStateChange(async appState => {
    if (appState !== 'active') {
      resetInitializationState();
    }
  });

  // Reset initialization state when the component is unmounted
  useEffectOnUnmount(resetInitializationState);
};
